/**
 * "API workspaces" on a project's page (project scope 1.4, several per project
 * since 21.10.2026): the workspaces this project holds, each with a remove ×,
 * and "Add workspace" — a dropdown of the ones it may still hold (every one
 * for a personal project, only those shared with the team for a team project).
 * A viewer sees the names only. The switcher follows on the next scope reload.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { bindWorkspace, boundWorkspaces, unbindWorkspace } from '../../scope/projectBinding';
import { bindingRefusal, holdsWorkspace } from '../../scope/projectBindingFields';
import { teamIdOfScope } from '../../graph/graphScopes';
import { refreshProjectScope } from '../../scope/projectScopeLoader';
import { enqueueScopeJob } from '../../scope/projectScopeJobs';
import { bumpGraph } from '../../graph/graphUiStore';
import './ProjectWorkspaceBinding.css';

/** The workspaces `project` may hold, by name. Pure. */
export function bindableWorkspaces(project, workspaces = []) {
  const teamId = teamIdOfScope(project?.scopeKey);
  return (workspaces || [])
    .filter((w) => w && !bindingRefusal(teamId, w))
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}

export default function ProjectWorkspaceBinding({ project, readOnly = false, onError }) {
  const { t } = useTranslation();
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const [busy, setBusy] = useState(false);
  const held = boundWorkspaces(project, workspaces);
  const addable = useMemo(
    () => bindableWorkspaces(project, workspaces).filter((w) => !holdsWorkspace(project, w.id)),
    [project, workspaces]
  );

  const run = async (fn) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      bumpGraph();
      await enqueueScopeJob(() => refreshProjectScope().catch(() => {}));
    } catch (err) {
      onError?.(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pj-binding" data-testid="project-workspace-binding">
      <span className="pj-binding-label">{t('projects.workspace.label')}</span>
      <ul className="pj-binding-list" aria-label={t('projects.workspace.label')}>
        {held.length === 0 && <li className="pj-binding-none">{t('projects.workspace.none')}</li>}
        {held.map((w) => (
          <li key={w.id} className="pj-binding-chip">
            <span>{w.name || String(w.id)}</span>
            {!readOnly && (
              <button
                type="button"
                className="pj-binding-remove"
                aria-label={t('projects.workspace.remove', { name: w.name || String(w.id) })}
                disabled={busy}
                onClick={() => run(() => unbindWorkspace(project, w.id))}
              >
                <X size={12} aria-hidden />
              </button>
            )}
          </li>
        ))}
      </ul>
      {!readOnly && addable.length > 0 && (
        <CompactSelectPicker
          className="pj-binding-picker"
          value=""
          onChange={(id) => {
            const ws = addable.find((w) => String(w.id) === String(id));
            if (ws) run(() => bindWorkspace(project, ws));
          }}
          options={[{ value: '', label: t('projects.workspace.add') }, ...addable.map((w) => ({ value: String(w.id), label: w.name || String(w.id) }))]}
          searchable={addable.length > 8}
          searchPlaceholder={t('projects.workspace.search')}
          menuMinWidth={220}
          disabled={busy}
          aria-label={t('projects.workspace.add')}
        />
      )}
    </div>
  );
}
